/* eslint-disable no-alert */
import React from 'react';
import { Button, Spinner } from 'reactstrap';
import { processPO, cancelPO, approvePO, rejectPO, printPO } from '../api';
import { alertError } from '../utils';

const GREEN = { backgroundColor: 'rgb(50, 178, 128)' };

const openDetail = (mode, label) => ({
  label,
  color: 'success',
  onClick: (po, ctx) => ctx.openDetail(po.id, mode),
});

const confirmed = (verb, request) => ({
  confirm: (po) => `Are you sure want to ${verb} ${po.code} ?`,
  request: (po) => request(po.id),
  refetch: true,
});

/**
 * Every button a list row can show. Either `onClick(po, ctx)` for instant
 * actions, or `request(po)` (optionally after `confirm(po)`) for backend
 * calls that show a spinner and, with `refetch`, reload the list.
 */
export const ACTIONS = {
  process: {
    label: (po) => (po.isCancel || po.isReject ? 'Re-Process' : 'Process'),
    color: 'success',
    hidden: (po, ctx) => !ctx.isFinance,
    ...confirmed('process', processPO),
  },
  cancel: {
    label: 'Cancel',
    color: 'danger',
    hidden: (po) => po.isCancel || po.isReject,
    ...confirmed('cancel', cancelPO),
  },
  approve: {
    label: 'Approve',
    color: 'success',
    ...confirmed('approve', approvePO),
  },
  reject: {
    label: 'Reject',
    color: 'danger',
    ...confirmed('reject', rejectPO),
  },
  print: { label: 'Print', color: 'primary', request: (po) => printPO(po.id) },
  upload: {
    label: 'Upload',
    color: 'primary',
    hidden: (po, ctx) => !ctx.isFinance,
    onClick: (po, ctx) => ctx.openUpload(po.id),
  },
  details: {
    label: 'Details',
    color: 'secondary',
    onClick: (po, ctx) => ctx.openDetail(po.id, ctx.tab.detailsMode),
  },
  liquid: openDetail('liquid', 'Liquid'),
  addToGl: openDetail('addToGl', 'Add To GL Journal'),
  editMutation: openDetail('editMutation', 'Edit'),
};

const RowActions = ({ po, ctx }) => {
  const { tab, isPending, run, refetch } = ctx;

  const handle = (name, action) => {
    if (action.onClick) {
      action.onClick(po, ctx);
      return;
    }
    if (action.confirm && !window.confirm(action.confirm(po))) return;
    run(name, po.id, async () => {
      try {
        await action.request(po);
        if (action.refetch) refetch();
      } catch (err) {
        alertError(err);
      }
    });
  };

  const visible = tab.rowActions.filter(
    (name) => !ACTIONS[name].hidden?.(po, ctx),
  );

  return (
    <div className="d-flex flex-column align-items-center">
      {visible.map((name, index) => {
        const action = ACTIONS[name];
        const label =
          typeof action.label === 'function' ? action.label(po) : action.label;
        return (
          <Button
            key={name}
            size="sm"
            color={action.color}
            className={index ? 'mt-1' : undefined}
            style={{ width: '90px', ...(action.color === 'success' && GREEN) }}
            onClick={() => handle(name, action)}
          >
            {isPending(name, po.id) ? (
              <Spinner size="sm" color="white" />
            ) : (
              label
            )}
          </Button>
        );
      })}
    </div>
  );
};

export default RowActions;
